import {
  FetchItemCacheKey,
  FetchItemsGqlQuery,
  FiltersViewGroups
} from '../../../../types';
import {
  FetchOperationsGqlQuery,
  FetchOperationsCacheKey,
  FetchOperationsFilters,
  FetchOperationsSort,
  FetchOperationsPage,
  FetchOperationsLimit
} from '../../operationsTypes';

import { useIndexQuery } from '../../../common/reactQuery/hooks/useIndexQuery';
import {
  useExtendedFilters,
  useExtendedFiltersInitialFilters
} from '../../../../app/hooks/useExtendedFilters';

import {
  INITIAL_OPERATIONS_FILTERS,
  INITIAL_OPERATIONS_LIMIT,
  INITIAL_OPERATIONS_PAGE,
  INITIAL_OPERATIONS_SORT
} from '../../operationsConstants';

interface PaginatedOperationsExtendedDefaultOptions {
  cacheKey: FetchOperationsCacheKey;
  query: FetchOperationsGqlQuery;
  filtersViewGroup?: FiltersViewGroups;
  initialPageFilters?: FetchOperationsFilters;
  initialTabFilters?: FetchOperationsFilters;
  initialSelectedFilters?: FetchOperationsFilters;
  defaultSettingsFilters?: FetchOperationsFilters;
  initialSort?: FetchOperationsSort;
  initialPage?: FetchOperationsPage;
  initialLimit?: FetchOperationsLimit;
  options?: {
    cacheTime?: number;
  };
}

interface PaginatedOperationsExtendedWithPrefetchItemOptions {
  fetchItemCacheKey: FetchItemCacheKey;
  fetchItemQuery: FetchItemsGqlQuery;
}

interface PaginatedOperationsExtendedWithoutPrefetchItemOptions {
  fetchItemCacheKey?: never;
  fetchItemQuery?: never;
}

type PaginatedOperationsExtendedOptions =
  PaginatedOperationsExtendedDefaultOptions &
    (
      | PaginatedOperationsExtendedWithPrefetchItemOptions
      | PaginatedOperationsExtendedWithoutPrefetchItemOptions
    );

const scope = 'operations';

function usePaginatedOperationsExtended<ItemType>({
  cacheKey,
  query,
  filtersViewGroup,
  initialPageFilters = INITIAL_OPERATIONS_FILTERS,
  initialTabFilters,
  initialSelectedFilters,
  defaultSettingsFilters,
  initialSort = INITIAL_OPERATIONS_SORT,
  initialPage = INITIAL_OPERATIONS_PAGE,
  initialLimit = INITIAL_OPERATIONS_LIMIT,
  fetchItemCacheKey,
  fetchItemQuery,
  options = {}
}: PaginatedOperationsExtendedOptions) {
  const { extendedInitialFilters, initialSettingsFilters } =
    useExtendedFiltersInitialFilters({
      filtersViewGroup,
      initialPageFilters,
      initialTabFilters,
      initialSelectedFilters,
      defaultSettingsFilters
    });

  const {
    data,
    items,
    itemsError,
    itemsTotalCount,
    meta,
    isFetched,
    isLoading,
    isPlaceholderData,
    currentPage,
    currentLimit,
    currentFilters,
    currentSort,
    updateItemCache,
    filterItems,
    prefetchItems,
    sortItems,
    paginateItems,
    limitItems,
    prefetchItem
  } = useIndexQuery<ItemType>({
    scope,
    query,
    cacheKey,
    initialFilters: extendedInitialFilters,
    initialLimit,
    initialPage,
    initialSort,
    fetchItemCacheKey,
    fetchItemQuery,
    options
  });

  const {
    // tabFilters,
    // settingsFilters,
    selectedFilters,
    // changeTabFilters,
    // changeSettingsFilters,
    // changeSettingsFiltersWithoutSave,
    changeSelectedFilters,
    resetSelectedFilters
    // resetSettingsFilters
  } = useExtendedFilters({
    viewGroup: filtersViewGroup,
    initialPageFilters,
    initialTabFilters,
    initialSettingsFilters,
    initialSelectedFilters,
    defaultSettingsFilters,
    currentFilters,
    filterItems
  });

  return {
    operationsData: data,
    operations: items,
    operationsError: itemsError,
    operationsTotalCount: itemsTotalCount,
    operationsMeta: meta,
    operationsFetched: isFetched,
    operationsLoading: isLoading,
    operationsIsPlaceholderData: isPlaceholderData,
    operationsFilters: currentFilters,
    operationsSort: currentSort,
    operationsPage: currentPage,
    operationsLimit: currentLimit,
    updateOperationCache: updateItemCache,
    // filterOperations: filterItems, // Do not ever use from extended version
    // changeOperationsFilters: changeItemsFilters, // Do not ever use from extended version
    // clearOperationsFiltersPersistInitial: clearItemsFiltersPersistInitial, // Do not ever use from extended version
    // clearOperationsFilters: clearItemsFilters, // Do not ever use from extended version
    sortOperations: sortItems,
    paginateOperations: paginateItems,
    limitOperations: limitItems,
    prefetchOperations: prefetchItems,
    prefetchOperation: prefetchItem,
    // operationsTabFilters: tabFilters,
    // operationsSettingsFilters: settingsFilters,
    operationsSelectedFilters: selectedFilters,
    // changeOperationsTabFilters: changeTabFilters,
    // changeOperationsSettingsFilters: changeSettingsFilters,
    // changeOperationsSettingsFiltersWithoutSave:
    //   changeSettingsFiltersWithoutSave,
    changeOperationsSelectedFilters: changeSelectedFilters,
    resetOperationsSelectedFilters: resetSelectedFilters
    // resetOperationsSettingsFilters: resetSettingsFilters
  };
}

export default usePaginatedOperationsExtended;
